import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PostbackService } from "@gen/tracker/v1/postback_connect";
import {
  ListPostbacksRequest,
  PostbackResult,
  type PostbackRecord,
  type UnmappedStatus,
} from "@gen/tracker/v1/postback_pb";
import { ConversionStatus } from "@gen/tracker/v1/conversion_pb";
import { DateRange, Pagination } from "@gen/tracker/v1/common_pb";

import { createClient } from "@/lib/connect";
import type { TKey } from "@/lib/i18n";
import type { Tone } from "@fox1ck-org/ui";

/** A status colour: the package's tones without the neutral and the brand ones. */
type StatusTone = Exclude<Tone, "neutral" | "primary">;

const client = createClient(PostbackService);

/**
 * The journal of every partner postback — the refused ones included — with
 * what became of each (доска Postbacks). Source: PostbackService over
 * tracker_edge_v2.postback_results.
 */
export const postbackKeys = {
  all: ["postbacks"] as const,
  list: (p: ListPostbacksParams) => [...postbackKeys.all, "list", p] as const,
  unmapped: (from: string, to: string, timezone: string) => [...postbackKeys.all, "unmapped", from, to, timezone] as const,
};

/** The URL word of each result tab, and back. */
export const RESULT_KEYS = ["accepted", "duplicate", "pending", "no_click", "unknown_status", "no_fx", "rejected", "bad_token"] as const;
export type ResultKey = (typeof RESULT_KEYS)[number];

const RESULT_OF: Record<ResultKey, PostbackResult> = {
  accepted: PostbackResult.ACCEPTED,
  duplicate: PostbackResult.DUPLICATE,
  pending: PostbackResult.PENDING,
  no_click: PostbackResult.NO_CLICK,
  unknown_status: PostbackResult.UNKNOWN_STATUS,
  no_fx: PostbackResult.NO_FX,
  rejected: PostbackResult.REJECTED,
  bad_token: PostbackResult.BAD_TOKEN,
};

export function resultOf(key: string): PostbackResult | undefined {
  return (RESULT_KEYS as readonly string[]).includes(key) ? RESULT_OF[key as ResultKey] : undefined;
}

export function resultKey(r: PostbackResult): ResultKey | undefined {
  return RESULT_KEYS.find((k) => RESULT_OF[k] === r);
}

/** How a result reads on the board: its label and its tone. */
export const RESULT_VIEW: Record<ResultKey, { label: TKey; tone: StatusTone | undefined }> = {
  accepted: { label: "pb.result.accepted", tone: "ok" },
  duplicate: { label: "pb.result.duplicate", tone: undefined },
  pending: { label: "pb.result.pending", tone: "info" },
  no_click: { label: "pb.result.no_click", tone: "warn" },
  unknown_status: { label: "pb.result.unknown_status", tone: "danger" },
  no_fx: { label: "pb.result.no_fx", tone: "danger" },
  rejected: { label: "pb.result.rejected", tone: "danger" },
  bad_token: { label: "pb.result.bad_token", tone: "danger" },
};

export interface ListPostbacksParams {
  from: string;
  to: string;
  /** The zone the days are drawn in — the workspace's. */
  timezone: string;
  result?: string;
  networkId?: string;
  campaignId?: string;
  search?: string;
  limit: number;
  offset: number;
}

export interface PostbacksPage {
  items: PostbackRecord[];
  total: number;
  /** Every result under the same filters except the result. */
  counts: Map<PostbackResult, number>;
  /** Their sum — the «Все» chip. */
  all: number;
}

export function useListPostbacks(p: ListPostbacksParams, options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: postbackKeys.list(p),
    placeholderData: keepPreviousData,
    refetchInterval: options.refetchInterval,
    queryFn: async (): Promise<PostbacksPage> => {
      const res = await client.listPostbacks(
        new ListPostbacksRequest({
          pagination: new Pagination({ limit: p.limit, offset: p.offset }),
          range: new DateRange({ from: p.from, to: p.to, timezone: p.timezone }),
          result: (p.result ? resultOf(p.result) : undefined) ?? PostbackResult.UNSPECIFIED,
          networkId: p.networkId ?? "",
          campaignId: p.campaignId ?? "",
          search: p.search ?? "",
        }),
      );
      const counts = new Map<PostbackResult, number>();
      let all = 0;
      for (const c of res.counts) {
        counts.set(c.result, Number(c.count));
        all += Number(c.count);
      }
      return { items: res.items, total: res.pagination?.total ?? res.items.length, counts, all };
    },
  });
}

/** Partner words nobody has mapped yet, with how many postbacks and USD each holds. */
export function useUnmappedStatuses(from: string, to: string, timezone: string, enabled = true) {
  return useQuery({
    queryKey: postbackKeys.unmapped(from, to, timezone),
    enabled,
    queryFn: async (): Promise<UnmappedStatus[]> =>
      (await client.listUnmappedStatuses({ range: new DateRange({ from, to, timezone }) })).items,
  });
}

/**
 * Apply postbacks again with today's policy. Only ever called from a button
 * the person pressed: a replay changes conversions, so nothing schedules it
 * on its own.
 */
export function useReplayPostbacks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventIds: string[]) => client.replayPostbacks({ eventIds }),
    onSettled: () => void qc.invalidateQueries({ queryKey: postbackKeys.all }),
  });
}

/** Teach the tracker one partner word, then (replay) apply what it left unmapped. */
export function useMapPostbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { networkId: string; rawStatus: string; status: ConversionStatus }) =>
      client.mapPostbackStatus({ ...v, replay: true }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: postbackKeys.all });
      void qc.invalidateQueries({ queryKey: ["data-health"] });
    },
  });
}

/**
 * The rows of a page the tracker will not try again by itself: no click and
 * given up, or unreadable. What «Повторить все исправленные» replays — a row
 * whose cause is still there gets the same result again (the ledger makes a
 * replay idempotent), so replaying the set is safe. Unmapped words are
 * replayed by mapping them; missing rates are backfilled when the rate
 * arrives; a wrong token never reached a conversion. Pure.
 */
export function replayable(rows: PostbackRecord[]): string[] {
  return rows
    .filter(
      (r) =>
        (r.result === PostbackResult.NO_CLICK && r.state === "dead") ||
        (r.result === PostbackResult.REJECTED && r.state !== "waiting"),
    )
    .map((r) => r.eventId);
}

/** Whether one row offers «Повторить» in its detail. Pure. */
export function canReplay(r: PostbackRecord): boolean {
  return replayable([r]).length === 1 || (r.result === PostbackResult.PENDING && r.state === "dead");
}
